/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * constants for claim filing dropdowns
 */

import { CODE_SET_MAPPING } from 'app-constants/codeExplorer';
import _ from 'lodash';

export const FORM_MODES = {
	PRIMARY_FILING: 'primaryFiling',
	SECONDARY_FILING: 'secondaryFiling',
	RESUBMIT: 'resubmit',
	VIEW_ONLY: 'viewOnly',
} as const;

const REASON_FOR_DELAY = [
	{
		codeValue: '1',
		content: 'Proof of Eligibility Unknown or Unavailable',
	},
	{
		codeValue: '2',
		content: 'Litigation',
	},
	{
		codeValue: '3',
		content: 'Authorization Delays',
	},
	{
		codeValue: '4',
		content: 'Delay in Certifying Provider',
	},
	{
		codeValue: '5',
		content: 'Delay in Supplying Billing Forms',
	},
	{
		codeValue: '6',
		content: 'Delay in Delivery of Custom-made Appliances',
	},
	{
		codeValue: '7',
		content: 'Third Party Processing Delay',
	},
	{
		codeValue: '8',
		content: 'Delay in Eligibility Determination',
	},
	{
		codeValue: '9',
		content: 'Original Claim Rejected or Denied Due to a Reason Unrelated to the Billing Limitation Rules',
	},
	{
		codeValue: '10',
		content: 'Administration Delay in the Prior Approval Process',
	},
	{
		codeValue: '11',
		content: 'Other',
	},
	{
		codeValue: '15',
		content: 'Natural Disaster',
	},
	{
		codeValue: '16',
		content: 'Lack of Information',
	},
	{
		codeValue: '17',
		content: 'No response to initial request',
	},
];

const REMARKS_DROPDOWN = [
	{
		codeValue: 'AAA',
		content: 'Agent Details',
	},
	{
		codeValue: 'AAB',
		content: 'Associated Business Areas',
	},
	{
		codeValue: 'AAC',
		content: 'Borrower',
	},
	{
		codeValue: 'AAD',
		content: 'Nationality Details',
	},
	{
		codeValue: 'AAE',
		content: 'Assets',
	},
	{
		codeValue: 'AAF',
		content: 'Liabilities',
	},
	{
		codeValue: 'AAH',
		content: 'Additional Facility Details',
	},
	{
		codeValue: 'AAI',
		content: 'Exemption Law Location Description',
	},
	{
		codeValue: 'AAJ',
		content: 'Forecast Details',
	},
	{ codeValue: 'AAK', content: 'Import and Export Details', paragraph_number: '1' },
	{
		codeValue: 'AAL',
		content: 'Inventory Valuation',
	},
	{
		codeValue: 'AAM',
		content: 'Product Brands Sold Description',
	},
	{
		codeValue: 'AAN',
		content: 'Purchase Territory',
	},
	{
		codeValue: 'AAO',
		content: 'Responsibilities',
	},
	{
		codeValue: 'AAP',
		content: 'Supplier Description',
	},
	{
		codeValue: 'AAQ',
		content: 'Education Description',
	},
	{
		codeValue: 'AAR',
		content: 'Liquidity Details',
	},
	{
		codeValue: 'AAS',
		content: 'Former Activity Description',
	},
	{
		codeValue: 'AAT',
		content: 'Division Description',
	},
	{
		codeValue: 'ABN',
		content: 'Abbreviated Nomenclature',
	},
	{
		codeValue: 'ACC',
		content: 'Access Instructions',

		notes: [
			{
				content:
					"Instructions or arrangements made with the customer on how to gain access to the customer's premises to work on a service request",
			},
		],
	},
	{
		codeValue: 'ACI',
		content: 'Additional Claim Information',
	},
	{
		codeValue: 'ACN',
		content: 'Action Taken',
	},
	{
		codeValue: 'ACS',
		content: 'Actual Solution',
	},
	{
		codeValue: 'ACT',
		content: 'Action',
	},
	{
		codeValue: 'ADD',
		content: 'Additional Information',
	},
	{
		codeValue: 'AES',
		content: 'Actual Evaluation Summary',
	},
	{
		codeValue: 'AET',
		content: 'Adverse Event Terms',
	},
	{
		codeValue: 'AFA',
		content: 'Description',
	},
	{
		codeValue: 'AFB',
		content: 'Generic Chemical Name',
	},
	{
		codeValue: 'AFC',
		content: 'Prevention Program Description',
	},
	{
		codeValue: 'AFD',
		content: 'Risk Management Plan Description',
	},
	{
		codeValue: 'AFE',
		content: 'Safety Comments',
	},
	{
		codeValue: 'AFF',
		content: 'Summary',
	},
	{
		codeValue: 'ALG',
		content: 'Allergies',
	},
	{
		codeValue: 'ALL',
		content: 'All Documents',
	},
	{
		codeValue: 'ALT',
		content: 'Alerts',
	},
	{
		codeValue: 'AMN',
		content: 'Additional Manufacturer Narrative',
	},
	{
		codeValue: 'AOO',
		content: 'Area of Operation',
	},
	{
		codeValue: 'APN',
		content: 'Application Notes',
	},
	{
		codeValue: 'APS',
		content: 'Appropriation Specifications',

		notes: [
			{
				content:
					'Multiformatted data that describes government accounting classification information used to process the payment information for services provided to the Government',
			},
		],
	},
	{
		codeValue: 'BBD',
		content: 'Bank Description',
	},
	{
		codeValue: 'BBF',
		content: 'Business Founder',
	},
	{
		codeValue: 'BBH',
		content: 'Business History',
	},
	{
		codeValue: 'BBN',
		content: 'Banking Notes',
	},
	{
		codeValue: 'BBO',
		content: 'Business Origin Description',
	},
	{
		codeValue: 'BBT',
		content: 'Brand Names',
	},
	{
		codeValue: 'BFD',
		content: 'Business Financing Details',
	},
	{
		codeValue: 'BOL',
		content: 'Bill of Lading Note',
	},
	{
		codeValue: 'BUR',
		content: 'Bureau Remarks',
	},
	{
		codeValue: 'CAA',
		content: 'Authentication Information',
	},
	{
		codeValue: 'CAB',
		content: 'Line of In-State Business',
	},
	{
		codeValue: 'CAC',
		content: 'Relationship Information',
	},
	{
		codeValue: 'CAD',
		content: 'Basis for Amount Due',
	},
	{
		codeValue: 'CAE',
		content: 'Type of Debt',
	},
	{
		codeValue: 'CAF',
		content: 'Land Use Purpose',
	},
	{
		codeValue: 'CAG',
		content: 'Land Description',
	},
	{
		codeValue: 'CAH',
		content: 'Basis of Calculation',
	},
	{
		codeValue: 'CAI',
		content: 'General Business Description',
	},
	{
		codeValue: 'CAJ',
		content: 'Type of Business',
	},
	{
		codeValue: 'CAK',
		content: 'Character of Business',
	},
	{
		codeValue: 'CAL',
		content: 'Representation of Value',
	},
	{
		codeValue: 'CAM',
		content: 'Supporting Statement, Tax, and Fee Computation',
	},
	{
		codeValue: 'CAN',
		content: 'Cooperative Corporation Statement',
	},
	{
		codeValue: 'CAO',
		content: 'Close Corporation Statement',
	},
	{
		codeValue: 'CAP',
		content: 'Agreement to Abide by Laws',
	},
	{
		codeValue: 'CAQ',
		content: 'Stock Restrictions',
	},
	{ codeValue: 'CAR', content: 'Other Related Information', paragraph_number: '1' },
	{
		codeValue: 'CAS',
		content: 'Prohibition Against Being an Officer',
	},
	{
		codeValue: 'CAT',
		content: 'Qualification of Director',
	},
	{ codeValue: 'CAU', content: 'Nature of Charter', paragraph_number: '1' },
	{
		codeValue: 'CAV',
		content: 'Statement of Assets and Liabilities',
	},
	{
		codeValue: 'CAW',
		content: 'Bankruptcy Information',
	},
	{
		codeValue: 'CAX',
		content: 'Certificate of Disclosure',
	},
	{
		codeValue: 'CAZ',
		content: 'Asset Detail',
	},
	{
		codeValue: 'CBA',
		content: 'Statement Related to Regulation',
	},
	{
		codeValue: 'CBB',
		content: 'Consideration to be Received',
	},
	{
		codeValue: 'CBC',
		content: 'Other Lawful Provisions',
	},
	{
		codeValue: 'CBH',
		content: 'Monetary Amount Description',
	},
	{
		codeValue: 'CBI',
		content: 'Description of Title',
	},
	{
		codeValue: 'CCA',
		content: 'Competition',
	},
	{
		codeValue: 'CCB',
		content: 'Construction Details',
	},
	{
		codeValue: 'CCC',
		content: 'Construction Financing',
	},
	{
		codeValue: 'CCD',
		content: 'Construction Line of Business',
	},
	{
		codeValue: 'CCE',
		content: 'Contract Details',
	},
	{ codeValue: 'CCF', content: 'Corporate Filing Details', paragraph_number: '1' },
	{
		codeValue: 'CCG',
		content: 'Customer Description',
	},
	{
		codeValue: 'CCN',
		content: 'Copyright Notice',
	},
	{
		codeValue: 'CDD',
		content: 'Contingent Debt Details',
	},
	{
		codeValue: 'CER',
		content: 'Certification Narrative',

		notes: [
			{
				content: 'Any notes associated with the certification involved',
			},
		],
	},
	{
		codeValue: 'CHG',
		content: 'Change',
	},
	{
		codeValue: 'CIG',
		content: 'Cigarette Information',
	},
	{
		codeValue: 'CIR',
		content: 'Circumstances Prior to Difficulty',
	},
	{
		codeValue: 'CLN',
		content: 'Classifying Information',
	},
	{
		codeValue: 'CLR',
		content: 'Security Clearance Instructions',

		notes: [
			{
				content: 'Used to describe the level of security clearance required for individuals viewing information',
			},
		],
	},
	{
		codeValue: 'CMP',
		content: 'Concomitant Medical Product Description',
	},
	{
		codeValue: 'CMT',
		content: 'Maintenance Comment',
	},
	{
		codeValue: 'COD',
		content: 'Corrected Data',
	},
	{
		codeValue: 'COM',
		content: 'Consumer Comments',
	},
	{
		codeValue: 'CON',
		content: 'Conviction Act Details',
	},
	{
		codeValue: 'CRA',
		content: 'Credit Report Alerts',
	},
	{
		codeValue: 'CRK',
		content: 'Closing Comment',
	},
	{
		codeValue: 'CRN',
		content: 'Credit Report Notes',
	},
	{
		codeValue: 'CUS',
		content: 'Customs declaration',
	},
	{
		codeValue: 'DCP',
		content: 'Goals, Rehabilitation Potential, or Discharge Plans',
	},
	{
		codeValue: 'DCS',
		content: 'Destination Control Statement',
	},
	{
		codeValue: 'DDC',
		content: 'Deficiency Description Changes',
	},
	{
		codeValue: 'DEE',
		content: 'Event Description',
	},
	{
		codeValue: 'DEL',
		content: 'Delivery',
	},
	{
		codeValue: 'DEP',
		content: 'Problem Description',
	},
	{
		codeValue: 'DFR',
		content: 'Dose, Frequency and Route Description',
	},
	{ codeValue: 'DFS', content: 'Departure from Specification Comment', paragraph_number: '1' },
	{
		codeValue: 'DGN',
		content: 'Diagnosis Description',

		notes: [
			{
				content: 'Verbal description of the condition involved',
			},
		],
	},
	{
		codeValue: 'DME',
		content: 'Durable Medical Equipment (DME) and Supplies',
	},
	{
		codeValue: 'DOD',
		content: 'Description of Damage',
	},
	{
		codeValue: 'DOI',
		content: 'Outcome Description',
	},
	{
		codeValue: 'DRV',
		content: 'Driver Out of Service Notice',
	},
	{
		codeValue: 'DRW',
		content: 'Driver Out of Service Resolution',
	},
	{
		codeValue: 'DSW',
		content: 'Detailed Statement of Work',
	},
	{
		codeValue: 'EAA',
		content: 'Other Type of Group',
	},
	{
		codeValue: 'EAB',
		content: 'Ballot Measure',
	},
	{
		codeValue: 'EAC',
		content: 'Attachment',
	},
	{
		codeValue: 'EAD',
		content: 'Board',
	},
	{
		codeValue: 'EAE',
		content: 'Prohibited Contribution Circumstance',
	},
	{
		codeValue: 'EAF',
		content: 'Committee Activity',
	},
	{
		codeValue: 'EAG',
		content: 'Compensation Arrangement',
	},
	{
		codeValue: 'EAH',
		content: 'Country Sub-Entity',
	},
	{
		codeValue: 'EAI',
		content: 'Faction',
	},
	{
		codeValue: 'EAJ',
		content: 'Gift',
	},
	{
		codeValue: 'EAK',
		content: 'In-Kind Contribution Use',
	},
	{
		codeValue: 'EAL',
		content: 'Industry Group',
	},
	{
		codeValue: 'EAM',
		content: 'Jurisdiction',
	},
	{
		codeValue: 'EAN',
		content: 'Nature and Purpose of Other Lobbyist Employers',
	},
	{
		codeValue: 'EAO',
		content: 'Description of Office',
	},
	{
		codeValue: 'EAP',
		content: 'Party Considering Legislation',
	},
	{
		codeValue: 'EAQ',
		content: 'Description of Position',
	},
	{
		codeValue: 'EAR',
		content: 'Description of Sponsor',
	},
	{
		codeValue: 'EAS',
		content: 'Affiliation',
	},
	{
		codeValue: 'EAT',
		content: 'Asset Disposition',
	},
	{
		codeValue: 'EAV',
		content: 'Committee Interest',
	},
	{
		codeValue: 'EAW',
		content: 'Compensation or Services Exchanged for Consideration',
	},
	{
		codeValue: 'EAX',
		content: 'Contributor Interest',
	},
	{
		codeValue: 'EAY',
		content: 'Description of Debt',
	},
	{
		codeValue: 'EAZ',
		content: 'Employer Description',
	},
	{
		codeValue: 'EBA',
		content: 'Purpose of Employment',
	},
	{
		codeValue: 'EBB',
		content: 'Description of Agency and Position',
	},
	{
		codeValue: 'EBC',
		content: 'Description of Goods and Services',
	},
	{
		codeValue: 'EBD',
		content: 'Length of Lobbying Actions',
	},
	{
		codeValue: 'EBE',
		content: 'Lobbying Interest',
	},
	{
		codeValue: 'EBF',
		content: 'Method of Disposal',
	},
	{
		codeValue: 'EBG',
		content: 'Purpose of Credit',
	},
	{
		codeValue: 'EBH',
		content: 'Description of Other Business Sub-Category',
	},
	{
		codeValue: 'EBI',
		content: 'Other Type of Compensation',
	},
	{
		codeValue: 'EBJ',
		content: 'Description of Other Legislative Interest',
	},
	{
		codeValue: 'EBK',
		content: 'Other Reason for Withdrawal',
	},
	{
		codeValue: 'EBL',
		content: 'Other Type of Relationship',
	},
	{
		codeValue: 'EBM',
		content: 'Other Temporary Residence',
	},
	{
		codeValue: 'EBN',
		content: 'Other Type of Committee',
	},
	{
		codeValue: 'EBO',
		content: 'Place Paid',
	},
	{
		codeValue: 'EBP',
		content: 'Proposition',
	},
	{
		codeValue: 'EBQ',
		content: 'Reason for Purchase',
	},
	{
		codeValue: 'EBR',
		content: 'Reason for Contribution',
	},
	{
		codeValue: 'EBS',
		content: 'Description of Repayment Schedule',
	},
	{
		codeValue: 'EBU',
		content: 'Service Description',
	},
	{
		codeValue: 'EBV',
		content: 'Initiative',
	},
	{
		codeValue: 'EBW',
		content: 'Description of Amendment',
	},
	{
		codeValue: 'EBX',
		content: 'Type of Election',
	},
	{
		codeValue: 'EBY',
		content: 'Other Type of Account',
	},
	{
		codeValue: 'EBZ',
		content: 'Interest Rate Description',
	},
	{
		codeValue: 'ECA',
		content: 'In-Kind Contribution',
	},
	{
		codeValue: 'ECB',
		content: 'Reason for Refund',
	},
	{
		codeValue: 'ECC',
		content: 'Incidental Expenses',
	},
	{
		codeValue: 'ECD',
		content: 'Environmental Conditions Description',
	},
	{
		codeValue: 'ECE',
		content: 'Other Expenses',
	},
	{
		codeValue: 'ECF',
		content: 'Other Income',
	},
	{
		codeValue: 'ECG',
		content: 'Description of Receipt',
	},
	{
		codeValue: 'ECH',
		content: 'Surplus Funds',
	},
	{
		codeValue: 'ECI',
		content: 'Collateral',
	},
	{
		codeValue: 'ECJ',
		content: 'Contributor',
	},
	{
		codeValue: 'ECK',
		content: 'Miscellaneous Receipt Transaction',
	},
	{
		codeValue: 'ECL',
		content: 'Other Advertising',
	},
	{
		codeValue: 'ECM',
		content: 'Estimate Comment',
	},
	{
		codeValue: 'ECN',
		content: 'Equipment Condition Description',
	},
	{
		codeValue: 'ECO',
		content: 'Other Necessary Personal Expense',
	},
	{
		codeValue: 'ECP',
		content: 'Other Campaign Expense',
	},
	{
		codeValue: 'ECQ',
		content: 'Long-Term Liability',
	},
	{
		codeValue: 'ECR',
		content: 'Short-Term Liability',
	},
	{
		codeValue: 'ECS',
		content: 'Other Sponsor Expense',
	},
	{
		codeValue: 'ECT',
		content: 'Emergency Certification',
	},
	{
		codeValue: 'ECU',
		content: 'Other Transportation Expense',
	},
	{
		codeValue: 'ECV',
		content: 'Refund',
	},
	{
		codeValue: 'ECW',
		content: 'Reason for Return',
	},
	{
		codeValue: 'EED',
		content: 'Equipment Description',
	},
	{
		codeValue: 'EFD',
		content: 'Equipment Function Description',
	},
	{
		codeValue: 'ELE',
		content: 'Equipment Log Entry',
	},
	{
		codeValue: 'EMC',
		content: 'Employment Comments',
	},
	{
		codeValue: 'EMD',
		content: 'Estimate Method Description',
	},
	{
		codeValue: 'ENR',
		content: 'Explanation for Non-Return of Device to Manufacturer',
	},
	{
		codeValue: 'ERN',
		content: 'Error Notes',
	},
	{
		codeValue: 'EVL',
		content: 'Event Location',
	},
	{
		codeValue: 'EXE',
		content: 'Exemption Description',
	},
	{
		codeValue: 'EXN',
		content: 'Exhibit Notes',
	},
	{
		codeValue: 'EXR',
		content: 'Exercise Routine',

		notes: [
			{
				content: 'Notes necessary to evaluate medical necessity of portable oxygen system',
			},
		],
	},
	{
		codeValue: 'EXT',
		content: 'Exterior Description',
	},
	{
		codeValue: 'FDD',
		content: 'Final Deficiency Description',
	},
	{
		codeValue: 'FEE',
		content: 'Fee Description',
	},
	{
		codeValue: 'FIX',
		content: 'Repair Summary',
	},
	{
		codeValue: 'FUT',
		content: 'Future Plans',
	},
	{
		codeValue: 'GEN',
		content: 'Entire Transaction Set',
	},
	{
		codeValue: 'GPI',
		content: 'General Product or Process Information',
	},
	{
		codeValue: 'GPL',
		content: 'General Policy',
	},
	{
		codeValue: 'GSI',
		content: 'General Specification Information',
	},
	{
		codeValue: 'HHI',
		content: 'Household Goods Information',
	},
	{
		codeValue: 'ICN',
		content: 'Interviewee Conversation',
	},
	{
		codeValue: 'IDT',
		content: 'Intangible Description',
	},
	{
		codeValue: 'IID',
		content: 'Inventory (Stock) Description',
	},
	{
		codeValue: 'IIE',
		content: 'Investment Description',
	},
	{ codeValue: 'IIR', content: 'Intercompany Relations', paragraph_number: '1' },
	{
		codeValue: 'IMP',
		content: 'Problem Impact',
	},
	{
		codeValue: 'INP',
		content: 'Reason for Inspection',
	},
	{
		codeValue: 'INS',
		content: 'Insurance',
	},
	{
		codeValue: 'INT',
		content: 'General Order Instructions',
	},
	{
		codeValue: 'INV',
		content: 'Invoice Instruction',
	},
	{
		codeValue: 'IVC',
		content: 'Income Verification Comments',
	},
	{
		codeValue: 'JVD',
		content: 'Joint Venture Description',
	},
	{
		codeValue: 'LAB',
		content: 'Labeling Instructions',
	},
	{
		codeValue: 'LBD',
		content: 'Laboratory Data',
	},
	{
		codeValue: 'LBS',
		content: 'Labeled Strength',
	},
	{
		codeValue: 'LEN',
		content: 'Lender Use',
	},
	{
		codeValue: 'LIN',
		content: 'Line Item',
	},
	{
		codeValue: 'LIQ',
		content: 'Liquor Information',
	},
	{
		codeValue: 'LLA',
		content: 'Letters of Liability Agreements',
	},
	{
		codeValue: 'LLB',
		content: 'Loan Details',
	},
	{
		codeValue: 'LLC',
		content: 'Long Term Debt Description',
	},
	{
		codeValue: 'LOC',
		content: 'Location Description Information',

		notes: [
			{
				content: 'A free-form description of a location, helpful in trying to locate a specific facility',
			},
		],
	},
	{
		codeValue: 'LOI',
		content: 'Loading Instructions',
	},
	{
		codeValue: 'LSD',
		content: 'Legal Structure Details',
	},
	{
		codeValue: 'MCD',
		content: 'Marital Contract Details',
	},
	{
		codeValue: 'MCN',
		content: 'Motor Carrier Instructions',
	},
	{
		codeValue: 'MDO',
		content: 'Device Operator Description',
	},
	{
		codeValue: 'MED',
		content: 'Medications',
	},
	{
		codeValue: 'MFG',
		content: 'Manufacturing Instructions',
	},
	{
		codeValue: 'MKN',
		content: 'Marketing Notes',
	},
	{
		codeValue: 'MMD',
		content: 'Merger Description',
	},
	{
		codeValue: 'MSD',
		content: 'Marketable Securities Description',
	},
	{
		codeValue: 'NCD',
		content: 'Nonconformance Specification',
	},
	{
		codeValue: 'NPD',
		content: 'Nameplate Data',
	},
	{
		codeValue: 'NTR',
		content: 'Nutritional Requirements',
	},
	{
		codeValue: 'OBI',
		content: 'Originator to Beneficiary Instructions',
	},
	{
		codeValue: 'OBL',
		content: 'Obligation Description',
	},
	{
		codeValue: 'OCA',
		content: 'Other Current Asset Description',
	},
	{
		codeValue: 'OCC',
		content: 'Occupancy Information',
	},
	{
		codeValue: 'OCL',
		content: 'Other Current Liability Description',
	},
	{
		codeValue: 'OCP',
		content: 'Occupation',
	},
	{
		codeValue: 'OCR',
		content: 'Outside the Continental U.S. (OCONUS) Rating Information',
	},
	{
		codeValue: 'ODD',
		content: 'Originator Deficiency Description',
	},
	{
		codeValue: 'ODT',
		content: 'Orders for Disciplines and Treatments',
	},
	{
		codeValue: 'OLS',
		content: 'Original Legal Structure',
	},
	{
		codeValue: 'OPO',
		content: 'Occupation Definition',
	},
	{
		codeValue: 'ORA',
		content: 'Test Results Other Than Room Air',

		notes: [
			{
				content: 'Description of the results of oxygen test when given with other than room air',
			},
		],
	},
	{
		codeValue: 'ORD',
		content: 'Ordering Restrictions',
	},
	{
		codeValue: 'ORE',
		content: 'Other Remedial Action',
	},
	{
		codeValue: 'ORI',
		content: 'Order Instructions',
	},
	{
		codeValue: 'OTH',
		content: 'Other Instructions',
	},
	{
		codeValue: 'OTN',
		content: 'Another Type of Number Description',
	},
	{
		codeValue: 'OTS',
		content: 'Report Source Description',
	},
	{
		codeValue: 'PAY',
		content: 'Payables',
	},
	{
		codeValue: 'PCS',
		content: 'Process Specification',
	},
	{
		codeValue: 'PDS',
		content: 'Product Specification',
	},
	{
		codeValue: 'PED',
		content: 'Employee Sharing Arrangements',
	},
	{
		codeValue: 'PEN',
		content: 'Penalty Description',
	},
	{
		codeValue: 'PES',
		content:
			'Partial Pressure of Oxygen (PO2) is 60 millimeters (MM) of Mercury (Hg) or above, or arterial blood oxygen saturation is 90% or above',

		notes: [
			{
				content: 'Description of the result of an oxygen saturation test',
			},
		],
	},
	{
		codeValue: 'PID',
		content: 'Property Improvement Description',
	},
	{
		codeValue: 'PKG',
		content: 'Packaging Instructions',
	},
	{
		codeValue: 'PMT',
		content: 'Payment',
	},
	{
		codeValue: 'POB',
		content: 'Primary Observation',
	},
	{
		codeValue: 'POC',
		content: 'Principals or Organization Comments',
	},
	{
		codeValue: 'POL',
		content: 'Property Owner Location Information',
	},
	{
		codeValue: 'PPC',
		content: 'Principal Procedure Code Description',
	},
	{
		codeValue: 'PRI',
		content: 'Priority',
	},
	{
		codeValue: 'PRN',
		content: 'Public Record Notes',
	},
	{
		codeValue: 'PRO',
		content: 'Previous Registered Office',
	},
	{
		codeValue: 'PRR',
		content: 'Price Range',
	},
	{
		codeValue: 'PSY',
		content: 'Problem Summary',
	},
	{
		codeValue: 'PUR',
		content: 'Purchasing',
	},
	{
		codeValue: 'QUL',
		content: 'Qualifications',
	},
	{
		codeValue: 'QUT',
		content: 'Quotation Instruction',
	},
	{
		codeValue: 'RDI',
		content: 'Reason for Delinquency Information',
	},
	{
		codeValue: 'REC',
		content: 'Recommendation',
	},
	{
		codeValue: 'REF',
		content: 'Treatment Refusal Note',
	},
	{
		codeValue: 'REG',
		content: 'Registered Activity',
	},
	{
		codeValue: 'REP',
		content: 'Report',
	},
	{
		codeValue: 'REV',
		content: 'Receivables',
	},
	{
		codeValue: 'RFL',
		content: 'Reason for Leaving',
	},
	{
		codeValue: 'RHB',
		content: 'Functional Limitations, Reason Homebound, or Both',
	},
	{
		codeValue: 'RLA',
		content: 'Reason for Leave of Absence',
	},
	{
		codeValue: 'RLH',
		content: 'Reasons Patient Leaves Home',
	},
	{
		codeValue: 'RNH',
		content: 'Times and Reasons Patient Not at Home',
	},
	{
		codeValue: 'RNI',
		content: 'Missing Report Explanation',
	},
	{
		codeValue: 'ROU',
		content: 'Circuit Routing Instructions',

		notes: [
			{
				content: 'A customer request for a particular routing sequence for a telecommunications circuit',
			},
		],
	},
	{
		codeValue: 'RPT',
		content: 'Report Remarks',
	},
	{
		codeValue: 'RST',
		content: 'Place Where a Report was Submitted',
	},
	{
		codeValue: 'RVC',
		content: 'Rent Verification Comments',
	},
	{
		codeValue: 'SAN',
		content: 'Settlement Amount Notes',
	},
	{
		codeValue: 'SCN',
		content: 'Ocean Shipping Container Information',
	},
	{
		codeValue: 'SDD',
		content: 'Sentence Description',
	},
	{
		codeValue: 'SET',
		content: 'Unusual Home, Social Environment, or Both',
	},
	{
		codeValue: 'SFM',
		content: 'Safety Measures',
	},
	{
		codeValue: 'SHR',
		content: 'Shipping Restrictions',
	},
	{
		codeValue: 'SMD',
		content: 'Selling Means Description',
	},
	{
		codeValue: 'SOB',
		content: 'Secondary Observation',
	},
	{
		codeValue: 'SOW',
		content: 'Statement of Work',
	},
	{
		codeValue: 'SPH',
		content: 'Special Handling',
	},
	{
		codeValue: 'SPT',
		content: 'Supplementary Plan of Treatment',
	},
	{
		codeValue: 'SPV',
		content: 'Closing Instructions',
	},
	{
		codeValue: 'SSA',
		content: 'School Attended Details',
	},
	{
		codeValue: 'SSC',
		content: 'Status Comment',
	},
	{
		codeValue: 'SSD',
		content: 'Sales Description',
	},
	{
		codeValue: 'SSE',
		content: 'Spouse Information',
	},
	{
		codeValue: 'SSG',
		content: 'School Graduated Details',
	},
	{
		codeValue: 'SSH',
		content: 'Security Service Information',
	},
	{
		codeValue: 'SSI',
		content: 'Shareholding Information',
	},
	{
		codeValue: 'SSS',
		content: 'Signing Authority',
	},
	{
		codeValue: 'SST',
		content: 'Sales Territory',
	},
	{
		codeValue: 'TAF',
		content: 'Tariff Abbreviation',
	},
	{
		codeValue: 'TCF',
		content: 'Tariff Commodity Footnotes',
	},
	{
		codeValue: 'TDA',
		content: 'Turkish Defense Affairs Authorization Information',
	},
	{
		codeValue: 'TES',
		content: 'Task Statement',
	},
	{
		codeValue: 'TIL',
		content: 'Tariff Index',
	},
	{
		codeValue: 'TLF',
		content: 'Tariff Rule',
	},
	{
		codeValue: 'TLR',
		content: 'Tradeline Remarks',
	},
	{
		codeValue: 'TMP',
		content: 'Toxic Organic Management Plan',
	},
	{
		codeValue: 'TPO',
		content: 'Third Party Organization Notes',
	},
	{
		codeValue: 'TRA',
		content: 'Transportation',
	},
	{
		codeValue: 'TRE',
		content: 'Reportable Event Description',
	},
	{
		codeValue: 'TRF',
		content: 'Tariff Rate Footnotes',
	},
	{
		codeValue: 'TRS',
		content: 'Quality Information',
	},
	{
		codeValue: 'TSD',
		content: 'Terms of Sale Description',
	},
	{
		codeValue: 'TSF',
		content: 'Tariff Section Footnotes',
	},
	{
		codeValue: 'TST',
		content: 'Test Results',
	},
	{
		codeValue: 'UPI',
		content: 'Updated Information',
	},
	{
		codeValue: 'VEC',
		content: 'Verification Comments',
	},
	{
		codeValue: 'VEH',
		content: 'Vehicle Out of Service Notice',
	},
	{
		codeValue: 'VNN',
		content: 'Variation Notes',
	},
	{
		codeValue: 'WHI',
		content: 'Warehouse Instruction',
	},
	{
		codeValue: 'WRP',
		content: 'Wrapping Instructions',
	},
	{
		codeValue: 'ZED',
		content: 'Zero Discharge Certification Statement',
	},
	{
		codeValue: 'ZZZ',
		content: 'Mutually Defined',
	},
];

const OTHER_TYPE_DROPDOWN = [
	{
		codeValue: 'ZZ',
		content: 'Mutually Defined',
	},
	{
		codeValue: '82',
		content: 'Rendering Provider',
	},
	{
		codeValue: 'DN',
		content: 'Referring Provider',
	},
];

const PROVIDER_TYPE_PROFESSIONAL = [
	{
		codeValue: 'DN',
		content: 'Referring Provider',
	},
	{
		codeValue: 'P3',
		content: 'Primary Care Provider',
	},
	{
		codeValue: '82',
		content: 'Rendering Provider',
	},
];

const PROVIDER_QUAL_OPTIONS = [
	// {
	// 	code: 'XX',
	// 	display: 'NPI',
	// },
	{
		code: '0B',
		display: 'State License Number',
	},
	{
		code: '1G',
		display: 'Provider UPIN Number',
	},
	{
		code: 'G2',
		display: 'Provider Commercial Number',
	},
	{
		code: 'LU',
		display: 'Location Number',
	},
];

const QUAL_DROPDOWN_OPTIONS = _.map(PROVIDER_QUAL_OPTIONS, (item) => ({
	label: item.display,
	value: item.code,
}));

const OTHER_TYPE_DROPDOWN_OPTIONS = _.map(OTHER_TYPE_DROPDOWN, (item) => ({
	label: item.content,
	value: item.codeValue,
}));

const REASON_FOR_DELAY_OPTIONS = _.map(REASON_FOR_DELAY, (item) => ({
	label: item.content,
	value: item.codeValue,
}));

const REMARKS_DROPDOWN_OPTIONS = _.map(REMARKS_DROPDOWN, (item) => ({
	label: item.content,
	value: item.codeValue,
}));

const PROVIDER_TYPE_PROFESSIONAL_OPTIONS = _.map(PROVIDER_TYPE_PROFESSIONAL, (item) => ({
	label: item.content,
	value: item.codeValue,
}));

const DRAFT_CLAIM_DISCARD = {
	title: 'Discard Draft',
	content: 'Are you sure you want to discard this draft claim? This action cannot be undone.',
	okText: 'Discard',
};

const INSTITUTIONAL_PROCEDURE_CODESETS = [CODE_SET_MAPPING.CPT, CODE_SET_MAPPING.HCPCS, CODE_SET_MAPPING.HIPPS];
const PROFESSIONAL_PROCEDURE_CODESETS = [CODE_SET_MAPPING.CPT, CODE_SET_MAPPING.HCPCS];

export {
	PROVIDER_QUAL_OPTIONS,
	REASON_FOR_DELAY_OPTIONS,
	REMARKS_DROPDOWN_OPTIONS,
	OTHER_TYPE_DROPDOWN_OPTIONS,
	DRAFT_CLAIM_DISCARD,
	INSTITUTIONAL_PROCEDURE_CODESETS,
	PROFESSIONAL_PROCEDURE_CODESETS,
	QUAL_DROPDOWN_OPTIONS,
	PROVIDER_TYPE_PROFESSIONAL_OPTIONS,
};
